<template>
  <div class="cashback-configuracao">
    <Loading :isActive="loading" />

    <CAlert v-if="erroCarga" color="danger" class="cashback-configuracao__alerta">
      <span>Não foi possível carregar as configurações: {{ erroCarga }}</span>
      <CButton color="danger" variant="outline" size="sm" @click="carregar">Tentar novamente</CButton>
    </CAlert>

    <CAlert v-if="erroValidacao" color="warning" closeButton @update:show="erroValidacao = null">{{ erroValidacao }}</CAlert>

    <CRow>
      <CCol lg="6" class="mb-3">
        <div class="cashback-configuracao__card">
          <div class="cashback-configuracao__cabecalho">
            <div class="cashback-configuracao__icone"><fa-icon icon="hand-holding-usd" /></div>
            <div>
              <div class="cashback-configuracao__titulo">Uso do saldo por pedido</div>
              <div class="cashback-configuracao__subtitulo">Quanto da carteira o cliente pode usar em cada pedido</div>
            </div>
            <span :class="['cashback-configuracao__situacao', { 'cashback-configuracao__situacao--ativa': possuiLimiteUso }]">
              {{ possuiLimiteUso ? "Limitado" : "Sem limite" }}
            </span>
          </div>
          <CAlert v-if="limitesDescartados.length" color="warning" class="cashback-configuracao__legado" role="alert">
            Esta configuração tem mais de um limite preenchido, mas só um limite é considerado. Está selecionado {{ tipoEscolhido.label }};
            ao salvar, os outros ({{ limitesDescartados.join(" e ") }}) serão removidos.
          </CAlert>
          <fieldset class="cashback-configuracao__limite-uso mb-2">
            <legend class="cashback-configuracao__rotulo">Tipo de limite</legend>
            <div class="cashback-configuracao__tipos-uso">
              <label
                v-for="tipo in tiposLimiteUso"
                :key="tipo.chave"
                :class="['cashback-configuracao__tipo-uso', { 'cashback-configuracao__tipo-uso--ativo': tipoLimite === tipo.chave }]"
              >
                <input
                  type="radio"
                  name="cashback-tipo-limite-uso"
                  class="cashback-configuracao__tipo-uso-opcao"
                  :value="tipo.chave"
                  :checked="tipoLimite === tipo.chave"
                  @change="tipoLimite = tipo.chave"
                />
                {{ tipo.label }}
              </label>
            </div>
          </fieldset>
          <div v-if="tipoEscolhido.campo" class="cashback-configuracao__valor-uso mb-2">
            <label class="cashback-configuracao__rotulo" for="cashback-limite-uso-valor">{{ tipoEscolhido.label }}</label>
            <InputPercent
              v-if="tipoEscolhido.percentual"
              id="cashback-limite-uso-valor"
              :key="`${tipoLimite}-${remontagem}`"
              :precision="2"
              :value="form[tipoEscolhido.campo] || 0"
              @input="form[tipoEscolhido.campo] = $event"
            />
            <InputMoney
              v-else
              id="cashback-limite-uso-valor"
              :key="`${tipoLimite}-${remontagem}`"
              :value="form[tipoEscolhido.campo] || 0"
              @input="form[tipoEscolhido.campo] = $event"
            />
          </div>
          <small class="cashback-configuracao__dica">
            Escolha no máximo um limite: só ele é considerado em cada pedido. Em Sem limite, o cliente usa todo o saldo, até o total do pedido.
          </small>
          <div class="cashback-configuracao__nota">
            <fa-icon icon="info-circle" class="cashback-configuracao__nota-icone" />
            <span>{{ exemploUso }}</span>
          </div>
        </div>
      </CCol>

      <CCol lg="6" class="mb-3">
        <div class="cashback-configuracao__card">
          <div class="cashback-configuracao__cabecalho">
            <div class="cashback-configuracao__icone"><fa-icon icon="wallet" /></div>
            <div>
              <div class="cashback-configuracao__titulo">Limite da carteira</div>
              <div class="cashback-configuracao__subtitulo">Saldo máximo que o cliente pode acumular</div>
            </div>
            <span :class="['cashback-configuracao__situacao', { 'cashback-configuracao__situacao--ativa': possuiLimiteCarteira }]">
              {{ possuiLimiteCarteira ? "Limitado" : "Sem limite" }}
            </span>
          </div>
          <CRow>
            <CCol sm="6" class="mb-2">
              <label class="cashback-configuracao__rotulo">Saldo máximo</label>
              <InputMoney :key="`carteira-${remontagem}`" :value="form.carteiraLimiteSaldo || 0" @input="form.carteiraLimiteSaldo = $event" />
            </CCol>
          </CRow>
          <div class="cashback-configuracao__nota">
            <fa-icon icon="info-circle" class="cashback-configuracao__nota-icone" />
            <span>{{ exemploCarteira }}</span>
          </div>
        </div>
      </CCol>

      <CCol lg="6" class="mb-3">
        <div class="cashback-configuracao__card">
          <div class="cashback-configuracao__cabecalho">
            <div class="cashback-configuracao__icone"><fa-icon icon="envelope" /></div>
            <div>
              <div class="cashback-configuracao__titulo">Avisos</div>
              <div class="cashback-configuracao__subtitulo">O que é avisado e quando</div>
            </div>
          </div>
          <div class="cashback-configuracao__lista">
            <div v-for="opcao in avisos" :key="opcao.campo" class="cashback-configuracao__opcao">
              <div class="cashback-configuracao__opcao-texto">
                <span class="cashback-configuracao__opcao-titulo">{{ opcao.titulo }}</span>
                <span class="cashback-configuracao__opcao-descricao">{{ opcao.descricao }}</span>
              </div>
              <label class="cashback-configuracao__chave">
                <input
                  type="checkbox"
                  role="switch"
                  :aria-label="opcao.titulo"
                  :aria-checked="form[opcao.campo] ? 'true' : 'false'"
                  :checked="form[opcao.campo]"
                  @change="form[opcao.campo] = $event.target.checked"
                />
                <span class="cashback-configuracao__chave-trilho"></span>
              </label>
            </div>
            <div class="cashback-configuracao__opcao" v-if="form.avisoVencimento">
              <div class="cashback-configuracao__opcao-texto">
                <span class="cashback-configuracao__opcao-titulo">Antecedência do aviso</span>
                <span class="cashback-configuracao__opcao-descricao">Dias antes do vencimento. Cada saldo é avisado uma única vez.</span>
              </div>
              <div class="cashback-configuracao__dias">
                <input
                  class="form-control"
                  type="number"
                  min="0"
                  step="1"
                  :max="diasMaximoAviso"
                  aria-label="Dias de antecedência do aviso"
                  v-model.number="form.avisoVencimentoDias"
                />
                <span>dias</span>
              </div>
            </div>
          </div>
        </div>
      </CCol>

      <CCol lg="6" class="mb-3">
        <div class="cashback-configuracao__card">
          <div class="cashback-configuracao__cabecalho">
            <div class="cashback-configuracao__icone"><fa-icon icon="users" /></div>
            <div>
              <div class="cashback-configuracao__titulo">Quem recebe os avisos no app</div>
              <div class="cashback-configuracao__subtitulo">O e-mail de cashback ganho vai sempre para o cliente</div>
            </div>
          </div>
          <div class="cashback-configuracao__lista">
            <div v-for="opcao in destinos" :key="opcao.campo" class="cashback-configuracao__opcao">
              <div class="cashback-configuracao__opcao-texto">
                <span class="cashback-configuracao__opcao-titulo"><fa-icon :icon="opcao.icone" class="mr-2 cashback-configuracao__opcao-icone" />{{ opcao.titulo }}</span>
                <span class="cashback-configuracao__opcao-descricao">{{ opcao.descricao }}</span>
              </div>
              <label class="cashback-configuracao__chave">
                <input
                  type="checkbox"
                  role="switch"
                  :aria-label="opcao.titulo"
                  :aria-checked="form[opcao.campo] ? 'true' : 'false'"
                  :checked="form[opcao.campo]"
                  @change="form[opcao.campo] = $event.target.checked"
                />
                <span class="cashback-configuracao__chave-trilho"></span>
              </label>
            </div>
          </div>
        </div>
      </CCol>

      <CCol v-if="modosUso.length" lg="12" class="mb-3">
        <div class="cashback-configuracao__card">
          <div class="cashback-configuracao__cabecalho">
            <div class="cashback-configuracao__icone"><fa-icon icon="exchange-alt" /></div>
            <div>
              <div id="cashback-modo-uso-titulo" class="cashback-configuracao__titulo">Forma de uso do saldo no ERP</div>
              <div class="cashback-configuracao__subtitulo">Como o saldo usado no pedido é enviado ao ERP</div>
            </div>
          </div>
          <CAlert v-if="!algumModoSuportado" color="danger" class="cashback-configuracao__legado" role="alert">
            O ERP desta empresa não suporta nenhuma forma de uso do saldo: o uso do saldo de cashback fica bloqueado nos canais. As demais configurações
            podem ser salvas normalmente e a forma atual é mantida.
          </CAlert>
          <CAlert v-else-if="modoSalvoBloqueado" color="danger" class="cashback-configuracao__legado" role="alert">
            A forma de uso salva não é suportada pelo ERP desta empresa: o uso do saldo de cashback está bloqueado nos canais até que uma forma disponível seja escolhida e salva.
          </CAlert>
          <div class="cashback-configuracao__modos-uso" role="radiogroup" aria-labelledby="cashback-modo-uso-titulo">
            <label
              v-for="modo in modosUso"
              :key="modo.chave"
              :class="[
                'cashback-configuracao__modo-uso',
                {
                  'cashback-configuracao__modo-uso--ativo': form.modoUso === modo.chave,
                  'cashback-configuracao__modo-uso--indisponivel': !modo.suportado,
                },
              ]"
            >
              <input
                type="radio"
                name="cashback-modo-uso"
                :value="modo.chave"
                :checked="form.modoUso === modo.chave"
                :disabled="!modo.suportado"
                @change="escolherModoUso(modo.chave)"
              />
              <span>
                <strong>{{ modo.label }}</strong>
                <small>{{ modo.descricao }}</small>
                <small v-if="!modo.suportado" class="cashback-configuracao__modo-uso-nota">Não disponível para o ERP desta empresa</small>
              </span>
            </label>
          </div>
        </div>
      </CCol>
    </CRow>

    <div class="cashback-configuracao__rodape">
      <span class="cashback-configuracao__pendente" v-if="alterado">
        <fa-icon icon="info-circle" class="mr-1" />Há alterações não salvas
      </span>
      <span class="cashback-configuracao__atualizacao" v-else-if="dataAtualizacao">Atualizado em {{ dataAtualizacao | datetime }}</span>
      <CButton color="primary" :disabled="loading || !!erroCarga || !alterado" @click="salvar">
        <fa-icon icon="save" class="mr-1" />Salvar configurações
      </CButton>
    </div>
  </div>
</template>

<script>
import { get, put } from "@/helpers/apiRequest";
import Loading from "@/components/Loading.vue";
import InputMoney from "@/components/InputMoney";
import InputPercent from "@/components/InputPercent";
import {
  DIAS_MAXIMO_AVISO,
  DURACAO_AVISO_SUCESSO_MS,
  LIMITE_USO_NENHUM,
  MENSAGEM_MODO_USO_BLOQUEADO,
  STATUS_VALIDACAO,
  TIPOS_LIMITE_USO,
  configuracaoVazia,
  exemploLimitesUso,
  formatMoney,
  limitesUsoPreenchidos,
  mensagemErroApi,
  modoUsoBloqueado,
  modosUsoDaApi,
  normalizarConfiguracao,
  payloadConfiguracao,
  positivo,
  preencherConfiguracao,
  tipoLimiteUso,
  tipoLimiteUsoPorChave,
  validarConfiguracao,
} from "./helpers";

export default {
  name: "ConfiguracoesAba",
  components: { Loading, InputMoney, InputPercent },

  data() {
    return {
      loading: false,
      erroCarga: null,
      erroValidacao: null,
      remontagem: 0,
      dataAtualizacao: null,
      diasMaximoAviso: DIAS_MAXIMO_AVISO,
      salvo: normalizarConfiguracao(configuracaoVazia()),
      form: configuracaoVazia(),
      tipoLimite: LIMITE_USO_NENHUM,
      tiposLimiteUso: TIPOS_LIMITE_USO,
      limitesCarregados: [],
      modosUso: [],
      avisos: [
        { campo: "avisoGanho", titulo: "Cashback ganho no app", descricao: "Avisa quando o cashback do pedido é confirmado no faturamento." },
        { campo: "avisoGanhoEmail", titulo: "Cashback ganho por e-mail", descricao: "Envia ao cliente o valor ganho, a validade e o saldo." },
        { campo: "avisoVencimento", titulo: "Saldo perto de vencer", descricao: "Avisa alguns dias antes de um saldo vencer." },
      ],
      destinos: [
        { campo: "avisoParaCliente", icone: "user", titulo: "Cliente", descricao: "Usuários do cliente no VersoCommerce e no VersoApp." },
        { campo: "avisoParaOperador", icone: "headset", titulo: "Operador", descricao: "Operador do cliente no VersoCall." },
        { campo: "avisoParaRepresentante", icone: "user-tie", titulo: "Representante", descricao: "Representante do cliente no VersoVendas." },
      ],
    };
  },

  computed: {
    tipoEscolhido() {
      return tipoLimiteUsoPorChave(this.tipoLimite);
    },

    limitesDescartados() {
      if (this.limitesCarregados.length < 2) return [];

      return this.limitesCarregados.filter((limite) => limite.chave !== this.tipoLimite).map((limite) => limite.label);
    },

    possuiLimiteUso() {
      return !!this.tipoEscolhido.campo && !!positivo(this.form[this.tipoEscolhido.campo]);
    },

    algumModoSuportado() {
      return this.modosUso.some((modo) => modo.suportado);
    },

    modoSalvoBloqueado() {
      return modoUsoBloqueado(this.modosUso, this.salvo.modoUso);
    },

    possuiLimiteCarteira() {
      return !!positivo(this.form.carteiraLimiteSaldo);
    },

    alterado() {
      if (this.tipoLimite !== tipoLimiteUso(this.salvo)) return true;

      return JSON.stringify(payloadConfiguracao(this.form, this.tipoLimite)) !== JSON.stringify(this.salvo);
    },

    exemploUso() {
      return exemploLimitesUso(this.form, this.tipoLimite);
    },

    exemploCarteira() {
      const limite = positivo(this.form.carteiraLimiteSaldo);

      if (!limite) return "Em branco, a carteira não tem limite de saldo.";

      return `Com ${formatMoney(limite)} de limite, o cashback gerado só é creditado até a carteira chegar a esse valor; o que passar não é creditado. A mudança vale para pedidos enviados depois dela: os já enviados recebem o Cashback garantido no envio e o saldo atual do cliente não é reduzido.`;
    },
  },

  beforeMount() {
    this.carregar();
  },

  methods: {
    carregar() {
      this.loading = true;
      this.erroCarga = null;

      return get("/admin/cashback/configuracao")
        .then((json) => this.preencher(json))
        .catch((err) => {
          this.erroCarga = mensagemErroApi(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    preencher(dados) {
      const campos = preencherConfiguracao(dados);

      this.form = campos;
      this.salvo = normalizarConfiguracao(campos);
      this.tipoLimite = tipoLimiteUso(campos);
      this.limitesCarregados = limitesUsoPreenchidos(campos);
      this.dataAtualizacao = (dados && dados.dataAtualizacao) || null;
      this.remontagem += 1;

      const modos = modosUsoDaApi(dados);

      if (modos.length) this.modosUso = modos;
    },

    escolherModoUso(modo) {
      const opcao = this.modosUso.find((item) => item.chave === modo);

      if (opcao && opcao.suportado) this.form.modoUso = modo;
    },

    salvar() {
      if (this.erroCarga) return;

      const exigeModoSuportado = this.modosUso.some((modo) => modo.suportado) && modoUsoBloqueado(this.modosUso, this.form.modoUso);

      this.erroValidacao = exigeModoSuportado ? MENSAGEM_MODO_USO_BLOQUEADO : validarConfiguracao(this.form, this.tipoLimite);

      if (this.erroValidacao) return;

      this.loading = true;
      const payload = payloadConfiguracao(this.form, this.tipoLimite);

      put("/admin/cashback/configuracao", payload)
        .then((json) => {
          this.preencher((json && json.data) || payload);
          this.$swal({ title: "Configurações salvas", icon: "success", timer: DURACAO_AVISO_SUCESSO_MS, showConfirmButton: false });
        })
        .catch((err) => {
          if (err && err.apiStatus === STATUS_VALIDACAO) {
            this.erroValidacao = mensagemErroApi(err);

            return;
          }

          this.$swal({ title: "Não foi possível salvar", text: mensagemErroApi(err), icon: "error" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.cashback-configuracao {
  padding: 16px 0 4px;
}

.cashback-configuracao__card {
  height: 100%;
  padding: 18px 20px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background-color: #fff;
}

.cashback-configuracao__cabecalho {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
}

.cashback-configuracao__icone {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background-color: rgba(var(--cashback-cor-primaria-rgb), 0.08);
  color: var(--cashback-cor-primaria);
}

.cashback-configuracao__titulo {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
}

.cashback-configuracao__subtitulo {
  font-size: 12px;
  color: #667085;
}

.cashback-configuracao__situacao {
  margin-left: auto;
  padding: 2px 10px;
  border-radius: 12px;
  background-color: #f2f4f7;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  color: #667085;
}

.cashback-configuracao__situacao--ativa {
  background-color: rgba(var(--cashback-cor-primaria-rgb), 0.1);
  color: var(--cashback-cor-primaria);
}

.cashback-configuracao__rotulo {
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #667085;
}

.cashback-configuracao__dica {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: #667085;
}

.cashback-configuracao__legado {
  margin-bottom: 12px;
  font-size: 12.5px;
}

.cashback-configuracao__limite-uso {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: none;
}

.cashback-configuracao__tipos-uso {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 3px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background-color: #f9fafb;
}

.cashback-configuracao__tipo-uso {
  position: relative;
  margin: 0;
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
  color: #667085;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.cashback-configuracao__tipo-uso-opcao {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.cashback-configuracao__tipo-uso--ativo {
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  color: var(--cashback-cor-primaria);
}

.cashback-configuracao__tipo-uso:focus-within {
  box-shadow: 0 0 0 3px rgba(var(--cashback-cor-primaria-rgb), 0.25);
}

.cashback-configuracao__modos-uso {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 8px;
}

.cashback-configuracao__modo-uso {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  cursor: pointer;
}

.cashback-configuracao__modo-uso input {
  margin-top: 3px;
}

.cashback-configuracao__modo-uso span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.cashback-configuracao__modo-uso small {
  color: #667085;
}

.cashback-configuracao__modo-uso--ativo {
  border-color: var(--cashback-cor-primaria, #3a3e81);
  background-color: var(--cashback-cor-primaria-suave, #eceaf3);
}

.cashback-configuracao__modo-uso--indisponivel {
  background-color: #f9fafb;
  cursor: not-allowed;
  opacity: 0.7;
}

.cashback-configuracao__modo-uso--indisponivel.cashback-configuracao__modo-uso--ativo {
  border-color: #f04438;
  background-color: #fef3f2;
  opacity: 1;
}

.cashback-configuracao__modo-uso .cashback-configuracao__modo-uso-nota {
  font-weight: 600;
  color: #b42318;
}

.cashback-configuracao__valor-uso {
  max-width: 240px;
}

.cashback-configuracao__alerta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.cashback-configuracao__nota {
  display: flex;
  gap: 8px;
  margin-top: 6px;
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #f8fafc;
  font-size: 12.5px;
  line-height: 1.45;
  color: #475467;
}

.cashback-configuracao__nota-icone {
  margin-top: 2px;
  flex-shrink: 0;
  color: #98a2b3;
}

.cashback-configuracao__lista {
  border: 1px solid #eaecf0;
  border-radius: 8px;
  overflow: hidden;
}

.cashback-configuracao__opcao {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 14px;
  border-bottom: 1px solid #eaecf0;
}

.cashback-configuracao__opcao:last-child {
  border-bottom: none;
}

.cashback-configuracao__opcao-texto {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cashback-configuracao__opcao-titulo {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
}

.cashback-configuracao__opcao-icone {
  color: #98a2b3;
}

.cashback-configuracao__opcao-descricao {
  font-size: 12px;
  color: #667085;
}

.cashback-configuracao__chave {
  position: relative;
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
}

.cashback-configuracao__chave input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.cashback-configuracao__chave-trilho {
  display: block;
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background-color: #d0d5dd;
  transition: background-color 0.15s ease;
}

.cashback-configuracao__chave-trilho::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.2);
  transition: transform 0.15s ease;
}

.cashback-configuracao__chave input:checked + .cashback-configuracao__chave-trilho {
  background-color: var(--cashback-cor-primaria);
}

.cashback-configuracao__chave input:checked + .cashback-configuracao__chave-trilho::after {
  transform: translateX(16px);
}

.cashback-configuracao__chave input:focus-visible + .cashback-configuracao__chave-trilho {
  box-shadow: 0 0 0 3px rgba(var(--cashback-cor-primaria-rgb), 0.25);
}

.cashback-configuracao__dias {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  font-size: 13px;
  color: #667085;
}

.cashback-configuracao__dias input {
  width: 80px;
}

.cashback-configuracao__rodape {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.cashback-configuracao__pendente {
  font-size: 12px;
  font-weight: 600;
  color: #b54708;
}

.cashback-configuracao__atualizacao {
  font-size: 12px;
  color: #98a2b3;
}

@media (max-width: 575px) {
  .cashback-configuracao__opcao {
    flex-wrap: wrap;
  }

  .cashback-configuracao__rodape {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
